import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Timer, Zap, CheckCircle, AlertTriangle, TrendingUp, TrendingDown,
  Info, ChevronDown, Target, Clock, Award
} from 'lucide-react';

const TestBehaviorCard = ({ testBehaviorAnalysis }) => {
  const [expanded, setExpanded] = useState('timeManagement');

  if (!testBehaviorAnalysis) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Test Behavior Analysis</h3>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-blue-800 dark:text-blue-200">
            No test behavior data available yet. Complete some tests to see detailed insights.
          </p>
        </div>
      </motion.div>
    );
  }

  const { timeManagement, accuracyPatterns, safeAttemptAnalysis, questionLevelInsights } = testBehaviorAnalysis;

  const toggleSection = (section) => {
    setExpanded(expanded === section ? null : section);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-purple-600 rounded-lg">
          <Zap className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Detailed Test Behavior Analysis</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Time management, accuracy patterns & safe attempt strategy</p>
        </div>
      </div>

      {/* Time Management Section */}
      <AccordionSection
        title="Time Management Analysis"
        icon={Timer}
        isExpanded={expanded === 'timeManagement'}
        onToggle={() => toggleSection('timeManagement')}
        badge={timeManagement.rushingIndicator > 30 ? { text: 'Needs Attention', color: 'warning' } : null}
      >
        <TimeManagementContent timeManagement={timeManagement} />
      </AccordionSection>

      {/* Accuracy Patterns Section */}
      <AccordionSection
        title="Accuracy Patterns"
        icon={CheckCircle}
        isExpanded={expanded === 'accuracyPatterns'}
        onToggle={() => toggleSection('accuracyPatterns')}
      >
        <AccuracyPatternsContent accuracyPatterns={accuracyPatterns} />
      </AccordionSection>

      {/* Safe Attempt Strategy Section */}
      <AccordionSection
        title="Safe Attempt Strategy"
        icon={Target}
        isExpanded={expanded === 'safeAttempt'}
        onToggle={() => toggleSection('safeAttempt')}
      >
        <SafeAttemptContent safeAttemptAnalysis={safeAttemptAnalysis} />
      </AccordionSection>

      {/* Question Level Insights Section */}
      <AccordionSection
        title="Topic-Wise Time & Accuracy Analysis"
        icon={Info}
        isExpanded={expanded === 'questionInsights'}
        onToggle={() => toggleSection('questionInsights')}
      >
        <QuestionInsightsContent questionLevelInsights={questionLevelInsights} />
      </AccordionSection>
    </motion.div>
  );
};

// Accordion Section Component
const AccordionSection = ({ title, icon: Icon, isExpanded, onToggle, badge, children }) => (
  <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <span className="font-semibold text-gray-900 dark:text-white">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {badge && (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            badge.color === 'warning' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : ''
          }`}>
            {badge.text}
          </span>
        )}
        <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </div>
    </button>
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="p-4 bg-white dark:bg-gray-800">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// Time Management Content Component
const TimeManagementContent = ({ timeManagement }) => (
  <div className="space-y-4">
    {/* Metrics Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Time Per Question</p>
        <p className="text-3xl font-bold text-blue-600">{timeManagement.averageTimePerQuestion}s</p>
      </div>
      <div className={`rounded-lg p-4 ${
        timeManagement.rushingIndicator > 30 
          ? 'bg-red-50 dark:bg-red-900/20' 
          : 'bg-green-50 dark:bg-green-900/20'
      }`}>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Rushing Indicator</p>
        <div className="flex items-center gap-2">
          <p className={`text-3xl font-bold ${
            timeManagement.rushingIndicator > 30 ? 'text-red-600' : 'text-green-600'
          }`}>
            {timeManagement.rushingIndicator}%
          </p>
          {timeManagement.rushingIndicator > 30 && <AlertTriangle className="h-5 w-5 text-red-600" />}
        </div>
        {timeManagement.rushingIndicator > 30 && (
          <p className="text-xs text-red-600 mt-1">You're rushing through questions too quickly</p>
        )}
      </div>
    </div>

    {/* Time Distribution */}
    <div>
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Time Distribution</h4>
      <div className="space-y-2">
        {Object.entries(timeManagement.timeDistribution).map(([range, count]) => {
          const total = Object.values(timeManagement.timeDistribution).reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={range} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400 w-24">{range}</span>
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white w-16 text-right">
                {count} ({percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>

    {/* Fast Questions Alert */}
    {timeManagement.fastQuestions.length > 0 && (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Zap className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h5 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Topics Where You're Rushing</h5>
            {timeManagement.fastQuestions.map((topic, index) => (
              <p key={index} className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">
                <strong>{topic.topic}</strong>: {topic.count} questions in avg {topic.averageTime}s (Accuracy: {topic.accuracy}%)
              </p>
            ))}
          </div>
        </div>
      </div>
    )}

    {/* Slow Questions Alert */}
    {timeManagement.slowQuestions.length > 0 && (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Topics Taking Too Much Time</h5>
            {timeManagement.slowQuestions.map((topic, index) => (
              <p key={index} className="text-sm text-blue-700 dark:text-blue-300 mb-1">
                <strong>{topic.topic}</strong>: {topic.count} questions in avg {topic.averageTime}s (Accuracy: {topic.accuracy}%)
              </p>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

// Accuracy Patterns Content Component
const AccuracyPatternsContent = ({ accuracyPatterns }) => (
  <div className="space-y-4">
    {/* Optimal Time Range */}
    {accuracyPatterns.optimalTimeRange && (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h5 className="font-semibold text-green-800 dark:text-green-200 mb-1">Your Optimal Time Range</h5>
            <p className="text-sm text-green-700 dark:text-green-300">
              <strong>{accuracyPatterns.optimalTimeRange}</strong> - You perform best when spending this amount of time per question
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Accuracy Dropoff */}
    {accuracyPatterns.accuracyDropoffPoint && (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <TrendingDown className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h5 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Accuracy Dropoff Detected</h5>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {accuracyPatterns.accuracyDropoffPoint.message}
            </p>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
              Accuracy drops by {accuracyPatterns.accuracyDropoffPoint.accuracyDrop}% at {accuracyPatterns.accuracyDropoffPoint.timeThreshold}
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Accuracy by Time Spent Table */}
    {accuracyPatterns.accuracyByTimeSpent.length > 0 && (
      <div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Accuracy vs Time Spent</h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">Time Range</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Accuracy</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Questions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {accuracyPatterns.accuracyByTimeSpent.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{item.timeRange}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.accuracy >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      item.accuracy >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {item.accuracy}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{item.questionCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Accuracy by Question Order */}
    {accuracyPatterns.accuracyByAttemptOrder.length > 0 && (
      <div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Accuracy by Question Order (Fatigue Analysis)</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {accuracyPatterns.accuracyByAttemptOrder.map((item, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{item.questionRange}</p>
              <p className={`text-lg font-bold ${
                item.accuracy >= 70 ? 'text-green-600' :
                item.accuracy >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {item.accuracy}%
              </p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Safe Attempt Content Component
const SafeAttemptContent = ({ safeAttemptAnalysis }) => (
  <div className="space-y-4">
    {/* Current vs Recommended */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Attempt Rate</p>
        <p className="text-3xl font-bold text-blue-600">{safeAttemptAnalysis.currentAttemptRate}%</p>
      </div>
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Recommended Attempt Rate</p>
        <p className="text-3xl font-bold text-green-600">{safeAttemptAnalysis.recommendedAttemptPercentage}%</p>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">For optimal accuracy</p>
      </div>
    </div>

    {/* Optimal Strategy */}
    {safeAttemptAnalysis.optimalAttemptStrategy && (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Award className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <h5 className="font-semibold text-green-800 dark:text-green-200 mb-2">Your Optimal Strategy</h5>
            <div className="space-y-1 text-sm text-green-700 dark:text-green-300">
              <p><strong>Attempt Range:</strong> {safeAttemptAnalysis.optimalAttemptStrategy.attemptRange}</p>
              <p><strong>Expected Score:</strong> {safeAttemptAnalysis.optimalAttemptStrategy.expectedScore}%</p>
              <p><strong>Expected Accuracy:</strong> {safeAttemptAnalysis.optimalAttemptStrategy.expectedAccuracy}%</p>
              <p className="italic mt-2 text-green-600 dark:text-green-400">
                {safeAttemptAnalysis.optimalAttemptStrategy.recommendation}
              </p>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Performance by Attempt Rate Table */}
    {safeAttemptAnalysis.accuracyByAttemptRate.length > 0 && (
      <div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Performance by Attempt Rate</h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">Attempt Range</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Avg Score</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Avg Accuracy</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Tests</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {safeAttemptAnalysis.accuracyByAttemptRate.map((item, index) => {
                const isOptimal = item.attemptRange === safeAttemptAnalysis.optimalAttemptStrategy?.attemptRange;
                return (
                  <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    isOptimal ? 'bg-green-50 dark:bg-green-900/20' : ''
                  }`}>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                      {item.attemptRange}
                      {isOptimal && (
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs">
                          Optimal
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.averageScore >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                        item.averageScore >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {item.averageScore}%
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{item.averageAccuracy}%</td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{item.testCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// Question Insights Content Component
const QuestionInsightsContent = ({ questionLevelInsights }) => (
  <div className="space-y-4">
    {/* Summary Stats */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Questions Analyzed</p>
        <p className="text-3xl font-bold text-blue-600">{questionLevelInsights.totalQuestions}</p>
      </div>
      <div className={`rounded-lg p-4 ${
        questionLevelInsights.averageAccuracy >= 70 
          ? 'bg-green-50 dark:bg-green-900/20' 
          : 'bg-yellow-50 dark:bg-yellow-900/20'
      }`}>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Overall Accuracy</p>
        <p className={`text-3xl font-bold ${
          questionLevelInsights.averageAccuracy >= 70 ? 'text-green-600' : 'text-yellow-600'
        }`}>
          {questionLevelInsights.averageAccuracy}%
        </p>
      </div>
    </div>

    {/* Topic-wise Analysis */}
    {questionLevelInsights.topicWiseTimeSpending.length > 0 && (
      <div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Topic-Wise Time Spending & Performance</h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">Topic</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Avg Time</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Accuracy</th>
                <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Questions</th>
                <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">Recommendation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {questionLevelInsights.topicWiseTimeSpending.map((topic, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2 font-semibold text-gray-900 dark:text-white">{topic.topic}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      topic.averageTime > 180 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                      topic.averageTime > 120 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      {topic.averageTime}s
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      topic.accuracy >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      topic.accuracy >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {topic.accuracy}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900 dark:text-white">{topic.questionCount}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate" title={topic.recommendation}>
                    {topic.recommendation}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Difficulty-wise Performance */}
    {questionLevelInsights.difficultyWisePerformance.length > 0 && (
      <div>
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Performance by Difficulty Level</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {questionLevelInsights.difficultyWisePerformance.map((diff, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 capitalize">{diff.difficulty} Questions</p>
              <p className="text-2xl font-bold text-blue-600 mb-2">{diff.accuracy}%</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Avg time: {diff.averageTime}s | {diff.questionCount} questions
              </p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default TestBehaviorCard;
